import { i18n } from '@/i18n/i18n'
import { AnalyticsService } from '@/pages/analytics/analyticsService'
import {
  type ApplicationConfig,
  type ApplicationConfigFontKeys,
  ApplicationConfigService,
  type TwilioConfig,
} from '@/shared/service/applicationConfig.service'
import { ErrorService } from '@/shared/service/errorService'
import { FilesService } from '@/shared/service/files.service'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'

import { commitAppConfig } from '../../shared/store/app-config.store'
import { keys } from '../../utils'
import { createStore, type GettersObj } from '../../utils/createStore'

export type GettersApplicationCfg = GettersObj<typeof mapGettersApplicationCfg>

export class ApplicationCfgState {
  applicationCfg: ApplicationConfig | null = null
  loading = false
  showAdvancedColors = false

  availableFonts: string[] = []
  fontChanges: { [k in ApplicationConfigFontKeys]?: File } = {}
}

export const {
  applicationCfg,
  commit: commitApplicationCfg,
  dispatch: dispatchApplicationCfg,
  mapGetters: mapGettersApplicationCfg,
  mapState: mapStateApplicationCfg,
  useGetter: useApplicationCfgGetter,
  useState: useApplicationCfgState,
} = createStore({
  namespaced: true,
  moduleName: 'applicationCfg',
  initState: new ApplicationCfgState(),
  mutations: {
    SET_APPLICATION_CFG(state, applicationCfg: ApplicationConfig) {
      state.applicationCfg = applicationCfg
    },

    SET_SHOW_ADVANCED_COLORS(state, showAdvancedColors: boolean) {
      state.showAdvancedColors = showAdvancedColors
    },

    LOAD(state) {
      state.loading = true
    },

    UNLOAD(state) {
      state.loading = false
    },

    SET_FONT_CHANGE(
      state,
      { file, key }: { file: File; key: ApplicationConfigFontKeys },
    ) {
      state.fontChanges[key] = file
    },
  },
  actions: {
    async loadApplicationCfg() {
      try {
        commitApplicationCfg('LOAD')
        const applicationCfg = await ApplicationConfigService.get()
        commitApplicationCfg('SET_APPLICATION_CFG', applicationCfg)
        commitAppConfig('SET_APP_CONFIG', applicationCfg)
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commitApplicationCfg('UNLOAD')
      }
    },

    async updateApplicationCfg({ state }, applicationCfg: ApplicationConfig) {
      try {
        commitApplicationCfg('LOAD')
        await Promise.all(
          keys(state.fontChanges).map(async (key) => {
            const font = applicationCfg[key]
            if (font != null) {
              const newFontId = await FilesService.uploadFont(
                state.fontChanges[key]!,
              )
              font.id = newFontId.filename
              font.postScriptName = newFontId.postScriptName
            }
          }),
        )
        await ApplicationConfigService.updateApplicationCfg(applicationCfg)
        SnackbarService.info(i18n.tc('APPLICATION_CFG.SAVE'))
        await dispatchApplicationCfg('loadApplicationCfg')
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commitApplicationCfg('UNLOAD')
      }
    },

    async updateApplicationCfgFeatures({}, applicationCfg: ApplicationConfig) {
      try {
        commitApplicationCfg('LOAD')
        await ApplicationConfigService.updateApplicationCfgFeatures(
          applicationCfg,
        )
        SnackbarService.info(i18n.tc('APPLICATION_CFG.SAVE'))
        await dispatchApplicationCfg('loadApplicationCfg')
      } catch (e) {
        SnackbarService.error((e as any)?.response?.data?.message)
      } finally {
        commitApplicationCfg('UNLOAD')
      }
    },

    async updateApplicationCfgTwilio({}, twilioConfig: TwilioConfig) {
      try {
        commitApplicationCfg('LOAD')
        await ApplicationConfigService.updateApplicationCfgTwilio(twilioConfig)
        SnackbarService.info(i18n.tc('APPLICATION_CFG.SAVE'))
        await dispatchApplicationCfg('loadApplicationCfg')
      } catch (e) {
        SnackbarService.error((e as any)?.response?.data?.message)
      } finally {
        commitApplicationCfg('UNLOAD')
      }
    },

    async shiftDates() {
      try {
        commitApplicationCfg('LOAD')
        await ApplicationConfigService.shiftDates()
        SnackbarService.info(i18n.tc('APPLICATION_CFG.SAVE'))
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commitApplicationCfg('UNLOAD')
      }
    },

    async forceMatomoInit() {
      await AnalyticsService.init()
      await dispatchApplicationCfg('loadApplicationCfg')
    },
  },
  getters: {
    getApplicationCfg: (state) => {
      return state.applicationCfg!
    },
  },
})
